import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ActivityCard = ({ activity }) => {
  const [isLiked, setIsLiked] = useState(activity?.isLiked || false);
  const [likeCount, setLikeCount] = useState(activity?.likes || 0);
  const [showComments, setShowComments] = useState(false);
  const navigate = useNavigate();

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const handleCharityClick = () => {
    navigate('/charity-profile');
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
            {activity?.donor?.name?.charAt(0)}
          </div>
          <div>
            <h3 className="font-semibold text-card-foreground">{activity?.donor?.name}</h3>
            <p className="text-sm text-muted-foreground">{formatTimeAgo(activity?.timestamp)}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm">
          <Icon name="MoreHorizontal" size={16} />
        </Button>
      </div>
      {/* Activity Content */}
      <div className="mb-4">
        <p className="text-card-foreground mb-3">{activity?.message}</p>
        
        {/* Charity Info */}
        <div 
          className="flex items-center space-x-3 p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-micro"
          onClick={handleCharityClick}
        >
          <div className="w-12 h-12 rounded-lg overflow-hidden">
            <Image 
              src={activity?.charity?.image} 
              alt={activity?.charity?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-card-foreground">{activity?.charity?.name}</h4>
            <p className="text-sm text-muted-foreground">{activity?.charity?.category}</p>
          </div>
          <div className="text-right">
            <p className="font-semibold text-primary">${activity?.amount}</p>
            <p className="text-xs text-muted-foreground">donated</p>
          </div>
        </div>

        {/* Impact Statement */}
        {activity?.impact && (
          <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Heart" size={16} className="text-success" />
              <p className="text-sm text-success font-medium">{activity?.impact}</p>
            </div>
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLike}
            className={`${isLiked ? 'text-error' : 'text-muted-foreground'}`}
          >
            <Icon name={isLiked ? "Heart" : "Heart"} size={16} className={isLiked ? "fill-current" : ""} />
            <span className="ml-1">{likeCount}</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowComments(!showComments)}
            className="text-muted-foreground"
          >
            <Icon name="MessageCircle" size={16} />
            <span className="ml-1">{activity?.comments || 0}</span>
          </Button>
          
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Icon name="Share2" size={16} />
          </Button>
        </div>
        
        <Button variant="outline" size="sm" onClick={handleCharityClick}>
          Support
        </Button>
      </div>
      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="space-y-3">
            {activity?.recentComments?.map((comment, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center text-xs">
                  {comment?.author?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    <span className="font-medium">{comment?.author}</span>
                    <span className="text-muted-foreground ml-2">{comment?.text}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex items-center space-x-2 mt-3">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground">
              J
            </div>
            <input
              type="text"
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <Button size="sm">Post</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityCard;